import {observable, computed, makeObservable, runInAction} from "mobx";
import type {AppState} from "../state/AppState";
import {Constants} from "../../ui/modules/space/spaceeditor/logic3d/Constants";
import type {SpaceFileDto, SpaceFileInsertionInfo, SpaceFileTileMode} from "../../generated/api/base";
import {XyiconFeature} from "../../generated/api/base";
import type {SpaceFilesResultDto} from "../../generated/api/search";
import type {IModel} from "./Model";
import type {Space} from "./Space";
import type {SpaceVersion} from "./SpaceVersion";

export class SpaceFile implements IModel {
	private readonly _parent: Space;
	private readonly _appState: AppState;
	private _isInsertionInfoModified: boolean = false;

	private _data: SpaceFileDto;

	constructor(data: SpaceFileDto, parent: Space, appState: AppState) {
		makeObservable<this, "_data">(this, {
			_data: observable,
			id: computed,
			thumbnailFileURL: computed,
			sourceFileURL: computed,
			spaceVersionId: computed,
			spaceVersion: computed,
			spaceVersionName: computed,
			insertionInfo: computed,
			parent: computed,
			mode: computed,
			tileSetId: computed,
			data: computed,
		});
		this._parent = parent;
		this._appState = appState;
		this.applyData(data);
	}

	public applyData(data: SpaceFileDto) {
		runInAction(() => {
			this._data = data;

			if (!this._data.settings) {
				this._data.settings = {};
			}

			if (!this._data.settings.insertionInfo) {
				this._data.settings.insertionInfo = {
					offsetX: 0,
					offsetY: 0,
					offsetZ: 0,
					width: Constants.DEFAULT_SPACE_WIDTH,
				};
			}

			if (!this._data.settings.insertionInfo.offsetZ) {
				this._data.settings.insertionInfo.offsetZ = 0;
			}
		});
	}

	public setInsertionInfo(insertionInfo: SpaceFileInsertionInfo) {
		this._data.settings.insertionInfo = {...this.insertionInfo, ...insertionInfo};
		this._isInsertionInfoModified = true;
	}

	public get id() {
		return this._data.spaceFileID;
	}

	public get thumbnailFileURL() {
		// in case it's not from the server, it's a generated URL (with canvas.toDataURL)
		return this._data.thumbnailFile.endsWith(".png")
			? this._appState.app.transport.getFullPathFromServer(`spaces/thumbnails/${this._data.thumbnailFile}`)
			: this._data.thumbnailFile;
	}

	public get sourceFileURL() {
		// in case it's not from the server, it's a generated URL (with URL.createObjectURL)
		return this._data.sourceFile.endsWith(".pdf")
			? this._appState.app.transport.getFullPathFromServer(`spaces/files/${this._data.sourceFile}`)
			: this._data.sourceFile;
	}

	public get spaceVersionId() {
		return this._data.spaceVersionID;
	}

	public set spaceVersionId(value: string) {
		this._data.spaceVersionID = value;
	}

	public get spaceVersion() {
		return this._appState.actions.getFeatureItemById(this.spaceVersionId, XyiconFeature.SpaceVersion) as SpaceVersion;
	}

	public set spaceVersion(value: SpaceVersion) {
		this.spaceVersion = value;
	}

	public get spaceVersionName() {
		return (this._data as SpaceFilesResultDto).spaceVersionName ?? this.spaceVersion?.name ?? "";
	}

	public get insertionInfo() {
		return this._data.settings.insertionInfo ?? {};
	}

	public get parent() {
		return this._parent;
	}

	public get isInsertionInfoModified() {
		return this._isInsertionInfoModified;
	}

	public get mode(): SpaceFileTileMode {
		return this._data.mode;
	}

	public get tileSetId() {
		return this._data.tileSetID;
	}

	public get data() {
		return this._data;
	}
}
